import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { useLocalSearchParams } from "expo-router";
import { Text, View } from "@src/components";
import { sizes } from "@src/constants/sizes";
import { isWeb } from "@src/utils/web-helpers";
import {
  RoadSenseReportNotFoundError,
  RoadSenseReportNotReadyError,
  useRoadSenseReport,
} from "@src/features/roadsense/hooks/useRoadSenseReport";
import { useRoadSenseWorkbookDownload } from "@src/features/roadsense/hooks/useRoadSenseWorkbookDownload";
import { RoadSenseReportView } from "@src/features/roadsense/components/RoadSenseReportView";
import { useTrafficFiltersParams } from "@src/features/roadsense/hooks/useTrafficFiltersParams";
import type { RoadSensePayload } from "@visioni/a3s-reports/client-messages";
import { instantsDayBounds, spanDays, type TrafficFilters } from "@src/features/roadsense/filters/traffic-filters";

/**
 * RoadSense **report detail** at
 * `…/reports/roadsense/report/<reportId>/site/<siteId>`.
 *
 * `reportId` is the report row's ULID — the report's canonical identity (see
 * the identity note in `get-report.ts`). `siteId` selects which of the
 * lifecycle's sites to render. Both are opaque to this screen: passed straight
 * to the fetch hook, never parsed. RoadSense reports are cumulative from an
 * asset's hire start, so `periodStart` is not a unique key and must not be used
 * to identify a report.
 */
export default function RoadSenseReportDetailScreen() {
  const params = useLocalSearchParams<{ reportId?: string; siteId?: string }>();
  // Expo Router types a param as `string | string[]` (a segment can repeat).
  // A single path segment is always a string in practice, but narrowing here
  // keeps the fetch hook from silently seeing an array.
  const reportId = firstParam(params.reportId);
  const siteId = firstParam(params.siteId);

  if (!isWeb) {
    return (
      <View flex={1} alignItems="center" justifyContent="center" padding="3xl" gap="md">
        <Text textStyle="sm/semibold" color="text" textAlign="center">
          RoadSense reports are only available on the web client.
        </Text>
        <Text textStyle="xs/regular" color="textMuted" textAlign="center">
          Open this report in your browser to view it.
        </Text>
      </View>
    );
  }

  return <LiveReport reportId={reportId} siteId={siteId} />;
}

/**
 * Expo Router params are `string | string[]` (a segment can repeat). Path
 * segments never repeat in practice, but this collapses to the first value (or
 * `undefined`) so the rest of the screen only ever deals with a plain string.
 */
function firstParam(value: string | string[] | undefined): string | undefined {
  return Array.isArray(value) ? value[0] : value;
}

const SCROLL_CONTENT = {
  paddingTop: 40,
  paddingBottom: 40,
  paddingHorizontal: 40,
  maxWidth: sizes.webReportMaxWidth,
  width: "100%",
} as const;

function LiveReport({ reportId, siteId }: { reportId: string | undefined; siteId: string | undefined }) {
  // `workbookS3Key` rides along on this single fetch (it's read from the same
  // listing-summary entry that yields the payload key), so the download button's
  // visibility is tied to the exact row that rendered the report — no second
  // by-id fetch that could fail independently and hide the button.
  const { report, payload, workbookS3Key, isLoading, isError, error, isTenantPending } = useRoadSenseReport(
    reportId,
    siteId,
  );
  const { filters, setFilters } = useTrafficFiltersParams(
    payload ? spanDays(payload.trend.instants) : undefined,
    payload ? (instantsDayBounds(payload.trend.instants) ?? undefined) : undefined,
  );

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={SCROLL_CONTENT} showsVerticalScrollIndicator={false}>
      <Body
        reportId={reportId}
        siteId={siteId}
        payload={payload}
        generatedAt={report?.generatedAt}
        workbookS3Key={workbookS3Key}
        isLoading={isLoading}
        isError={isError}
        error={error}
        isTenantPending={isTenantPending}
        filters={filters}
        onFiltersChange={setFilters}
      />
    </ScrollView>
  );
}

interface BodyProps {
  reportId: string | undefined;
  siteId: string | undefined;
  payload: RoadSensePayload | undefined;
  /** ISO instant the report was last generated — passed to the hero's "Generated At" line. */
  generatedAt: string | undefined;
  workbookS3Key: string | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  isTenantPending: boolean;
  filters: TrafficFilters;
  onFiltersChange: (next: TrafficFilters) => void;
}

function Body({
  reportId,
  siteId,
  payload,
  generatedAt,
  workbookS3Key,
  isLoading,
  isError,
  error,
  isTenantPending,
  filters,
  onFiltersChange,
}: BodyProps) {
  if (!reportId || !siteId) {
    return (
      <Message
        title="Invalid report"
        body="This URL is missing a site or report id. Pick a report from the list to continue."
      />
    );
  }

  if (isTenantPending) {
    return <Loading label="Resolving tenant…" />;
  }

  if (isLoading && !payload) {
    return <Loading label="Loading report…" />;
  }

  if (isError && error instanceof RoadSenseReportNotFoundError) {
    return (
      <Message
        title="Report not found"
        body="No RoadSense report exists for this id. It may have been deleted or never generated."
      />
    );
  }

  if (isError && error instanceof RoadSenseReportNotReadyError) {
    const detail =
      error.status === "failed" ?
        "Report is unavailable at the moment. Please reload in a few minutes."
      : "Report is being generated. Please reload in a few minutes.";
    return <Message title="Report not ready" body={detail} />;
  }

  if (isError) {
    // Deliberately generic copy — the raw API error body (e.g. a 422 validation
    // JSON blob) is not user-facing. The actionable cases (not found / not ready)
    // are handled above with specific guidance.
    return (
      <Message
        title="Couldn't load report"
        body="Something went wrong loading this report. Please refresh the page, or pick another report from the list."
      />
    );
  }

  if (!payload) {
    // Decode failed (logged by the hook) — surface a generic message so the
    // screen never falls through to a blank container. We still offer the
    // workbook download: reaching here means the three-hop fetch *succeeded*
    // (so `workbookS3Key` is the valid key from the same row) and only the
    // render `.json` failed schema validation. The `.xlsx` is a separate
    // artefact that doesn't depend on the render schema, so frontend payload
    // drift must not cost the user their workbook access.
    return (
      <View gap="lg">
        <Message
          title="Report unavailable"
          body="The report payload could not be decoded. The schema may be out of date — please reload the page. You can still download the workbook below."
        />
        <DownloadWorkbookButton reportId={reportId} workbookS3Key={workbookS3Key} />
      </View>
    );
  }

  return (
    <View gap="lg">
      <DownloadWorkbookButton reportId={reportId} workbookS3Key={workbookS3Key} />
      <RoadSenseReportView
        payload={payload}
        generatedAt={generatedAt}
        filters={filters}
        onFiltersChange={onFiltersChange}
      />
    </View>
  );
}

/**
 * "Download workbook" affordance for the rendered report. Hidden when the site
 * has no `.xlsx` key (shouldn't happen for a ready report, but the listing
 * summary is plugin-defined data we don't fully control). Presigning happens on
 * click via {@link useRoadSenseWorkbookDownload} so the URL is fresh.
 */
function DownloadWorkbookButton({ reportId, workbookS3Key }: { reportId: string; workbookS3Key: string | undefined }) {
  const { download, isDownloading, error } = useRoadSenseWorkbookDownload(reportId, workbookS3Key);
  if (!workbookS3Key) return null;

  return (
    <View gap="xs" alignItems="flex-end">
      <Pressable
        onPress={isDownloading ? undefined : download}
        accessibilityRole="button"
        accessibilityState={{ disabled: isDownloading }}
        style={{ paddingVertical: 8, paddingHorizontal: 16 }}
      >
        {isDownloading ?
          <View flexDirection="row" gap="sm" alignItems="center">
            <ActivityIndicator />
            <Text textStyle="xs/regular" color="textMuted">
              Preparing download…
            </Text>
          </View>
        : <Text textStyle="xs/semibold" color="text">
            Download workbook (.xlsx)
          </Text>
        }
      </Pressable>
      {error ?
        <Text textStyle="xs/regular" color="textMuted">
          Download failed. Please try again.
        </Text>
      : null}
    </View>
  );
}

interface MessageProps {
  title: string;
  body: string;
}

function Message({ title, body }: MessageProps) {
  return (
    <View padding="3xl" alignItems="center" gap="sm">
      <Text textStyle="sm/semibold" color="text" textAlign="center">
        {title}
      </Text>
      <Text textStyle="xs/regular" color="textMuted" textAlign="center">
        {body}
      </Text>
    </View>
  );
}

interface LoadingProps {
  label: string;
}

function Loading({ label }: LoadingProps) {
  return (
    <View padding="3xl" alignItems="center" gap="md">
      <ActivityIndicator />
      <Text textStyle="sm/regular" color="textMuted">
        {label}
      </Text>
    </View>
  );
}
